import React from "react";
import { Box, Flex, Icon, Stack, Text } from "@chakra-ui/react";
import { MdCloud } from "react-icons/md";
import StatusChipIndicator from "./StatusChipIndicator";
import { useSectionColors } from "./statusCardStyles";

/**
 * Displays ElasticSearch connection info: status chip, cluster name, and version.
 * @param {object} props
 * @param {object|null} props.elasticsearch - Elasticsearch status data from API
 * @returns {React.ReactElement}
 */
function ElasticInfoSection({ elasticsearch }) {
    const { boxBg, title } = useSectionColors();
    return (
        <Box>
            <Flex align="center" gap="8px" mb="12px">
                <Icon as={MdCloud} w="20px" h="20px" color="blue.400" />
                <Text fontSize="md" fontWeight="600" color={title}>ElasticSearch</Text>
            </Flex>
            <Box p="14px" bg={boxBg} borderRadius="12px">
                <Stack spacing="10px">
                    <ElasticConnectionRow connected={elasticsearch?.connected} />
                    <ElasticStatRow label="Cluster" value={elasticsearch?.info?.cluster_name || "N/A"} />
                    <ElasticStatRow label="Version" value={elasticsearch?.info?.version || "N/A"} />
                </Stack>
            </Box>
        </Box>
    );
}

/**
 * Renders the connection status row with chip indicator.
 * @param {object} props
 * @param {boolean} props.connected - Whether ES is connected
 * @returns {React.ReactElement}
 */
function ElasticConnectionRow({ connected }) {
    const { label } = useSectionColors();
    return (
        <Flex justify="space-between" align="center">
            <Text fontSize="sm" color={label}>Connection</Text>
            <StatusChipIndicator isActive={connected} activeLabel="Connected" inactiveLabel="Disconnected" />
        </Flex>
    );
}

/**
 * Generic stat row with label and text value.
 * @param {object} props
 * @param {string} props.label - Row label
 * @param {string} props.value - Row value
 * @returns {React.ReactElement}
 */
function ElasticStatRow({ label, value }) {
    const colors = useSectionColors();
    return (
        <Flex justify="space-between" align="center">
            <Text fontSize="sm" color={colors.label}>{label}</Text>
            <Text fontSize="sm" fontWeight="600" color={colors.value}>{value}</Text>
        </Flex>
    );
}

export default ElasticInfoSection;
